/**
 * Sidebar density mode for the permanent app shell.
 * Saved default (Preferences → Default navigation): icon | expanded | collapsed.
 * - ☰ cycle / double-click change the session UI only (not the saved default).
 * - Cycle order: expanded → icon → collapsed.
 * - collapsed session: ☰ opens a temporary flyout; double-click returns to expanded.
 * Platform default (no explicit DB save): icons-only.
 */

export const SIDEBAR_MODES = ['expanded', 'icon', 'collapsed'];
/** Platform default when the account has not explicitly saved sidebar_mode. */
export const DEFAULT_SIDEBAR_MODE = 'icon';
/** v4: account default must not inherit stale expanded from older caches. */
export const SIDEBAR_MODE_STORAGE_KEY = 'sidebar-mode-v4';
const LEGACY_STORAGE_KEYS = [
  'sidebar-mode',
  'sidebar-mode-v2',
  'sidebar-mode-v3',
  'sidebar-preview-mode-v2',
  'sidebar-mode-platform-default-heal-v1',
];

export const SIDEBAR_MODE_LABELS = {
  expanded: 'Nav bar',
  icon: 'Icons only',
  collapsed: 'Hamburger',
};

export function isValidSidebarMode(mode) {
  return SIDEBAR_MODES.includes(mode);
}

function clearLegacySidebarModeKeys() {
  try {
    LEGACY_STORAGE_KEYS.forEach((key) => localStorage.removeItem(key));
  } catch {
    /* ignore */
  }
}

export function getStoredSidebarMode() {
  try {
    clearLegacySidebarModeKeys();
    const saved = localStorage.getItem(SIDEBAR_MODE_STORAGE_KEY);
    if (saved && isValidSidebarMode(saved)) return saved;
  } catch {
    /* ignore */
  }
  return DEFAULT_SIDEBAR_MODE;
}

export function setStoredSidebarMode(mode) {
  if (!isValidSidebarMode(mode)) return;
  try {
    clearLegacySidebarModeKeys();
    localStorage.setItem(SIDEBAR_MODE_STORAGE_KEY, mode);
  } catch {
    /* ignore */
  }
}

/**
 * Resolve account default from ui_preferences.
 * Honors expanded/collapsed only when the server marks sidebar_mode_explicit.
 * Otherwise always icons-only (ignores stale session/local expanded).
 */
export function resolveAccountSidebarMode(prefs) {
  if (!prefs || typeof prefs !== 'object') return DEFAULT_SIDEBAR_MODE;
  // Explicit false from API, or missing flag with no reason to trust client cache
  if (prefs.sidebar_mode_explicit === false) return DEFAULT_SIDEBAR_MODE;
  if (prefs.sidebar_mode_explicit === true && isValidSidebarMode(prefs.sidebar_mode)) {
    return prefs.sidebar_mode;
  }
  // Legacy payloads without the flag: only trust icon/collapsed; treat expanded as unset
  if (isValidSidebarMode(prefs.sidebar_mode) && prefs.sidebar_mode !== 'expanded') {
    return prefs.sidebar_mode;
  }
  return DEFAULT_SIDEBAR_MODE;
}

/** Account default navigation density from session/user prefs. */
export function getAccountSidebarMode(user = null) {
  try {
    const raw = sessionStorage.getItem('user');
    if (raw) {
      const prefs = JSON.parse(raw)?.ui_preferences;
      if (prefs) return resolveAccountSidebarMode(prefs);
    }
    if (user?.ui_preferences) return resolveAccountSidebarMode(user.ui_preferences);
  } catch {
    /* ignore */
  }
  return DEFAULT_SIDEBAR_MODE;
}

export function applySidebarMode(mode) {
  const next = isValidSidebarMode(mode) ? mode : DEFAULT_SIDEBAR_MODE;
  const el = document.querySelector('.App');
  if (el) el.setAttribute('data-sidebar-mode', next);
  return next;
}

/** Cycle rail densities: expanded → icon → collapsed → (double-click) expanded. */
export function cycleSidebarMode(mode) {
  if (mode === 'expanded') return 'icon';
  if (mode === 'icon') return 'collapsed';
  return 'expanded';
}

export function sidebarModeButtonTitle(mode) {
  if (mode === 'collapsed') {
    return 'Open navigation menu · double-click for full sidebar';
  }
  if (mode === 'expanded') return 'Full sidebar · click for icons only';
  return 'Icons only · click for hamburger';
}
